/**
 * ParticipantDetailPage — sender's view of their own container
 * participation. Shows status timeline, declared values, dispatch
 * proof upload (when status=awaiting_supplier_dispatch), customs fee
 * + receipt (when set), barcode label info, withdraw button.
 *
 * Mirrors Flutter `containers/participant_detail_page.dart`.
 *   GET /api/cargo/containers/participants/:pid
 *   POST .../dispatch-proof
 *   POST .../withdraw
 */
import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowRight, Hash, Loader, Truck, Receipt, X, MapPin, Package,
  Building2, AlertCircle, DollarSign,
} from 'lucide-react';
import {
  useParticipant, useUploadDispatchProof, useWithdrawParticipant,
} from '@/hooks/useContainers';
import { AppLayout } from '@/components/layout/AppLayout';
import { Button } from '@/components/common/Button';
import { ConfirmDialog } from '@/components/common/Dialog';
import { participantStatusDisplay } from '@/utils/containerStatus';

const FIELD_CLASSES =
  'w-full rounded-xl border border-border bg-surface px-4 py-3 text-text-primary outline-none transition focus:border-primary';

// Lifecycle progression — happy path. Side branches (rejected/withdrawn/
// exception_*) render their own banner.
const STATUS_FLOW = [
  'join_requested',
  'approved',
  'awaiting_supplier_dispatch',
  'pending_warehouse_confirmation',
  'at_origin_warehouse',
  'loaded_in_container',
  'sealed',
  'in_transit',
  'at_destination_port',
  'customs_filed_with_fee',
  'customs_cleared',
  'arrived_at_destination_warehouse',
  'handed_to_final_mile',
  'delivered',
] as const;

const TERMINAL_BAD = ['rejected', 'withdrawn', 'exception_terminal', 'refunded'] as const;

export default function ParticipantDetailPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pid } = useParams<{ pid: string }>();
  const participantId = pid ? Number(pid) : null;

  const { data: participant, isLoading } = useParticipant(participantId);
  const upload = useUploadDispatchProof();
  const withdraw = useWithdrawParticipant();

  const [showProofForm, setShowProofForm] = useState(false);
  const [proofPhotos, setProofPhotos] = useState('');  // comma-separated URLs
  const [proofNote, setProofNote] = useState('');
  const [showWithdraw, setShowWithdraw] = useState(false);
  const [withdrawError, setWithdrawError] = useState<string | null>(null);

  if (isLoading) {
    return (
      <AppLayout>
        <div className="flex justify-center py-16 text-text-tertiary">
          <Loader size={32} className="animate-spin" />
        </div>
      </AppLayout>
    );
  }
  if (!participant) {
    return (
      <AppLayout>
        <p className="text-center text-text-secondary py-16">
          {t('participant_not_found', 'سجل المشاركة غير موجود.')}
        </p>
      </AppLayout>
    );
  }

  const sd = participantStatusDisplay(participant.status);
  const isTerminalBad = (TERMINAL_BAD as readonly string[]).includes(participant.status);
  const canUploadProof = participant.status === 'awaiting_supplier_dispatch' && !participant.dispatch_proof_url;
  const canWithdraw = participant.status === 'join_requested' || participant.status === 'awaiting_supplier_dispatch';
  const currentIdx = STATUS_FLOW.indexOf(participant.status as typeof STATUS_FLOW[number]);

  return (
    <AppLayout>
      <button
        onClick={() => navigate(-1)}
        className="flex items-center gap-2 text-text-secondary mb-4 hover:text-primary"
      >
        <ArrowRight size={18} /> {t('back', 'رجوع')}
      </button>

      <div className="max-w-2xl mx-auto space-y-4">
        {/* Header */}
        <div className="bg-surface border border-border rounded-2xl p-5">
          <div className="flex items-center justify-between gap-3 mb-2">
            <div className="flex items-center gap-2">
              <Hash size={14} className="text-text-tertiary" />
              <span className="font-bold text-text-primary" dir="ltr">{participant.public_code}</span>
            </div>
            <span className={`text-xs px-3 py-1.5 rounded-full font-bold ${sd.color}`}>{sd.label}</span>
          </div>
          {participant.title && (
            <h1 className="text-base font-bold text-text-primary mb-1">{participant.title}</h1>
          )}
          {participant.container_title && (
            <div className="text-sm text-text-secondary mb-2">
              {t('in_container', 'ضمن حاوية')}: <strong>{participant.container_title}</strong>
              {participant.container_public_code && (
                <span className="text-text-tertiary ml-1" dir="ltr">({participant.container_public_code})</span>
              )}
            </div>
          )}
          <div className="flex flex-wrap gap-3 text-xs text-text-tertiary">
            <span><Package size={11} className="inline" /> {participant.pieces_count} {t('pieces', 'قطع')}</span>
            <span>{participant.est_weight_kg} kg</span>
            {participant.est_cbm != null && <span>{participant.est_cbm} m³</span>}
            <span>${Number(participant.declared_value_usd).toLocaleString()}</span>
            {participant.category && <span>· {participant.category}</span>}
          </div>
          {participant.price_total_usd != null && participant.price_total_usd > 0 && (
            <div className="mt-3 pt-3 border-t border-border/50 flex items-center justify-between gap-3">
              <span className="text-xs text-text-secondary">{t('total_price', 'السعر النهائي')}</span>
              <span className="text-xl font-bold text-primary">
                ${Number(participant.price_total_usd).toLocaleString()}
              </span>
            </div>
          )}
        </div>

        {/* Destination */}
        <div className="bg-surface border border-border rounded-2xl p-4 space-y-2">
          <h2 className="text-sm font-bold text-text-secondary mb-2 flex items-center gap-2">
            <MapPin size={14} /> {t('delivery_destination', 'وجهة التسليم')}
          </h2>
          {participant.destination_contact_name && (
            <div className="text-sm">
              <span className="text-text-tertiary">{t('receiver_name', 'الاسم')}: </span>
              <strong>{participant.destination_contact_name}</strong>
            </div>
          )}
          <div className="text-sm">
            <span className="text-text-tertiary">{t('phone', 'الهاتف')}: </span>
            <span dir="ltr">{participant.destination_contact_phone}</span>
          </div>
          {participant.destination_city_name && (
            <div className="text-sm">
              <span className="text-text-tertiary">{t('city', 'المدينة')}: </span>
              {participant.destination_city_name}
            </div>
          )}
          <div className="text-sm">
            <span className="text-text-tertiary">{t('address', 'العنوان')}: </span>
            {participant.destination_address_text}
          </div>
          <div className="text-xs text-text-tertiary">
            {t('delivery_mode', 'وضع التسليم')}: <strong>{participant.delivery_mode === 'door' ? t('door_to_door', 'لباب البيت') : t('pickup_at_warehouse', 'استلام من المستودع')}</strong>
          </div>
        </div>

        {/* Rejected / withdrawn / exception banner */}
        {isTerminalBad && (
          <div className="bg-error/10 border border-error/30 rounded-2xl p-4 text-error text-sm">
            <strong className="block mb-1 flex items-center gap-1">
              <AlertCircle size={14} /> {sd.label}
            </strong>
            {participant.rejection_reason && <p>{participant.rejection_reason}</p>}
            {participant.cancel_reason && <p>{participant.cancel_reason}</p>}
            {participant.exception_reason && <p>{participant.exception_reason}</p>}
          </div>
        )}

        {/* Lifecycle Timeline */}
        {!isTerminalBad && currentIdx >= 0 && (
          <div className="bg-surface border border-border rounded-2xl p-5">
            <h2 className="text-sm font-bold text-text-secondary mb-3">
              {t('status_progress', 'التقدّم')}
            </h2>
            <div className="space-y-1">
              {STATUS_FLOW.map((s, i) => {
                const done = i <= currentIdx;
                const current = i === currentIdx;
                const dsp = participantStatusDisplay(s);
                return (
                  <div key={s} className="flex items-center gap-3">
                    <div className={`w-3 h-3 rounded-full flex-shrink-0 ${
                      done
                        ? current ? 'bg-primary ring-4 ring-primary/20' : 'bg-success'
                        : 'bg-border'
                    }`} />
                    <span className={`text-sm ${
                      done ? 'text-text-primary font-semibold' : 'text-text-tertiary'
                    }`}>
                      {dsp.label}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Dispatch proof upload — sender step after approval */}
        {canUploadProof && (
          <div className="bg-surface border border-border rounded-2xl p-5">
            <h2 className="text-sm font-bold text-text-secondary mb-2 flex items-center gap-2">
              <Truck size={16} /> {t('dispatch_proof_title', 'إثبات إرسال البضاعة للمستودع')}
            </h2>
            <p className="text-xs text-text-tertiary mb-3">
              {t('dispatch_proof_hint', 'بعد إرسال بضاعتك للمستودع، ارفع صور الإيصال + رقم التتبّع لينتقل الطلب لتأكيد المنظِّم.')}
            </p>
            {!showProofForm ? (
              <Button variant="primary" onClick={() => setShowProofForm(true)}>
                <Receipt size={14} className="ml-1" />
                {t('upload_proof', 'رفع إثبات')}
              </Button>
            ) : (
              <div className="space-y-3">
                <Field label={t('proof_photos', 'روابط صور (واحد لكل سطر)') + ' *'}>
                  <textarea
                    value={proofPhotos}
                    onChange={(e) => setProofPhotos(e.target.value)}
                    className={FIELD_CLASSES + ' min-h-[80px]'}
                    placeholder="https://...jpg"
                    dir="ltr"
                  />
                </Field>
                <Field label={t('note_optional', 'ملاحظة (اختياري)')}>
                  <textarea
                    value={proofNote}
                    onChange={(e) => setProofNote(e.target.value)}
                    className={FIELD_CLASSES + ' min-h-[60px]'}
                  />
                </Field>
                <div className="flex gap-2">
                  <Button
                    variant="primary"
                    loading={upload.isPending}
                    disabled={!proofPhotos.trim()}
                    onClick={async () => {
                      const photos = proofPhotos.split(/\s+/).filter((u) => u.startsWith('http'));
                      if (photos.length === 0) return;
                      await upload.mutateAsync({
                        pid: participant.id,
                        photos,
                        note: proofNote.trim() || undefined,
                      });
                      setShowProofForm(false);
                      setProofPhotos(''); setProofNote('');
                    }}
                  >
                    {t('submit', 'إرسال')}
                  </Button>
                  <Button variant="ghost" onClick={() => setShowProofForm(false)} type="button">
                    {t('cancel', 'إلغاء')}
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Already uploaded — show readonly */}
        {participant.dispatch_proof_url && (
          <div className="bg-success/5 border border-success/30 rounded-2xl p-4">
            <h2 className="text-sm font-bold text-success mb-1 flex items-center gap-2">
              <Truck size={14} /> {t('proof_uploaded', 'إثبات الإرسال مرفوع')}
            </h2>
            <a
              href={participant.dispatch_proof_url}
              target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-1 text-xs text-primary hover:underline mt-1"
            >
              <Receipt size={12} /> {t('view_receipt', 'عرض الإيصال')}
            </a>
            {participant.dispatch_proof_uploaded_at && (
              <div className="text-xs text-text-tertiary mt-1">
                {new Date(participant.dispatch_proof_uploaded_at).toLocaleString('ar-SY')}
              </div>
            )}
          </div>
        )}

        {/* Customs fee */}
        {participant.customs_fee_usd != null && (
          <div className="bg-surface border border-border rounded-2xl p-4">
            <h2 className="text-sm font-bold text-text-secondary mb-2 flex items-center gap-2">
              <DollarSign size={14} /> {t('customs_fee', 'الرسوم الجمركية')}
            </h2>
            <div className="text-lg font-bold text-text-primary">
              ${Number(participant.customs_fee_usd).toLocaleString()}
            </div>
            {participant.customs_receipt_url && (
              <a href={participant.customs_receipt_url} target="_blank" rel="noreferrer"
                className="inline-flex items-center gap-1 text-xs text-primary hover:underline mt-1">
                <Receipt size={11} /> {t('view_customs_receipt', 'عرض إيصال الرسوم')}
              </a>
            )}
            {participant.customs_fee_paid_at && (
              <div className="text-xs text-success mt-1">
                {t('paid_at', 'مدفوعة')}: {new Date(participant.customs_fee_paid_at).toLocaleDateString('ar-SY')}
              </div>
            )}
          </div>
        )}

        {/* Package barcode */}
        {participant.package_barcode && (
          <div className="bg-surface border border-border rounded-2xl p-4">
            <h2 className="text-sm font-bold text-text-secondary mb-1 flex items-center gap-2">
              <Building2 size={14} /> {t('package_barcode', 'رمز الطرد')}
            </h2>
            <div className="font-mono text-base bg-background rounded p-2 text-center" dir="ltr">
              {participant.package_barcode}
            </div>
            {participant.package_barcode_at && (
              <div className="text-xs text-text-tertiary mt-1 text-center">
                {t('issued_at', 'صدر')}: {new Date(participant.package_barcode_at).toLocaleString('ar-SY')}
              </div>
            )}
          </div>
        )}

        {/* Withdraw */}
        {canWithdraw && (
          <Button
            variant="ghost"
            onClick={() => { setWithdrawError(null); setShowWithdraw(true); }}
          >
            <X size={14} className="ml-1" />
            {t('cancel_participation', 'إلغاء الانضمام')}
          </Button>
        )}

        <ConfirmDialog
          open={showWithdraw}
          title={t('cancel_participation', 'إلغاء الانضمام')}
          body={t('cancel_participation_confirm', 'إلغاء طلب الانضمام؟ لا يمكن التراجع.')}
          destructive
          loading={withdraw.isPending}
          errorMessage={withdrawError}
          confirmLabel={t('confirm_cancel', 'نعم، إلغاء')}
          onCancel={() => setShowWithdraw(false)}
          onConfirm={async () => {
            try {
              await withdraw.mutateAsync({ pid: participant.id });
              setShowWithdraw(false);
              navigate('/containers/my', { replace: true });
            } catch (e: any) {
              setWithdrawError(e?.message || 'فشل الإلغاء');
            }
          }}
        />
      </div>
    </AppLayout>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="text-xs text-text-secondary mb-1 block">{label}</span>
      {children}
    </label>
  );
}
